import type { GridFilterItem, GridFilterModel, GridApiPro, GridLogicOperator } from '@mui/x-data-grid-pro';
import { isActiveFilterItem } from 'components/common/dataGrid/filters/activity';
import { GetDefaultFilterItemFn } from './types';

export function createEmptyFilterModel(): GridFilterModel {
  return { items: [] };
}

export function getDeselectedFilterModel(
  apiRef: { current: GridApiPro },
  excludeFieldFromCount?: string,
  getDefaultFilterItem?: GetDefaultFilterItemFn
): GridFilterModel {
  const items: GridFilterItem[] = [];

  if (excludeFieldFromCount) {
    const currentModel = apiRef.current.state?.filter?.filterModel;
    const fixedFilter = currentModel?.items?.find(
      (item) => item.field === excludeFieldFromCount
    );
    if (fixedFilter) {
      items.push(fixedFilter);
    }
  }

  if (getDefaultFilterItem) {
    const defaultItem = getDefaultFilterItem(apiRef);
    if (defaultItem) {
      items.push(defaultItem);
    }
  }

  return { items };
}

export function getSecondVisibleFilterableColumn(
  apiRef: { current: GridApiPro }
): { field: string; operator: string } | null {
  const columns = apiRef.current.getAllColumns();
  const visibilityModel = apiRef.current.state?.columns?.columnVisibilityModel ?? {};

  const visibleFilterableColumns = columns.filter((col) => {
    const isVisible = visibilityModel[col.field] !== false;
    const isFilterable = col.filterable !== false;
    return isVisible && isFilterable;
  });

  const secondColumn = visibleFilterableColumns[1];
  if (!secondColumn) return null;

  const operators = secondColumn.filterOperators ?? [];
  const firstOperator = operators[0]?.value ?? 'contains';

  return {
    field: secondColumn.field,
    operator: firstOperator,
  };
}

export function createDefaultFilterItem(
  apiRef: { current: GridApiPro }
): GridFilterItem | null {
  const columnInfo = getSecondVisibleFilterableColumn(apiRef);
  if (!columnInfo) return null;

  return {
    id: `default-filter-${Date.now()}`,
    field: columnInfo.field,
    operator: columnInfo.operator,
    value: undefined,
  };
}

export function getEffectiveFilters(
  items: GridFilterModel['items'],
  excludeField?: string
): GridFilterModel['items'] {
  return items
    .filter((item) => !excludeField || item.field !== excludeField)
    .filter(isActiveFilterItem);
}

/** A row without its id, with every other field (a grid may add its own, e.g. the group of an issue filter row). */
function describeFilterItem({ id: _id, ...item }: GridFilterItem): string {
  return JSON.stringify(Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b))));
}

/** The model's fields other than its rows and quick filter: the logical operator and the grid's own (issue groups). */
type FilterModelWithGroupSettings = GridFilterModel & {
  groupLogicOperators?: Record<string, string>;
};

function describeFilterModelSettings({
  items: _items,
  quickFilterValues: _quickFilterValues,
  quickFilterLogicOperator: _quickFilterLogicOperator,
  quickFilterExcludeHiddenColumns: _quickFilterExcludeHiddenColumns,
  // MUI's default; imported as a type only so that this module stays free of the data grid at runtime.
  logicOperator = 'and' as GridLogicOperator,
  ...gridSettings
}: FilterModelWithGroupSettings): string {
  const { groupLogicOperators, ...otherGridSettings } = gridSettings;
  const sortedGroupLogicOperators = groupLogicOperators
    ? Object.fromEntries(Object.entries(groupLogicOperators).sort(([a], [b]) => a.localeCompare(b)))
    : undefined;

  return JSON.stringify({
    logicOperator,
    ...otherGridSettings,
    ...(sortedGroupLogicOperators && Object.keys(sortedGroupLogicOperators).length > 0
      ? { groupLogicOperators: sortedGroupLogicOperators }
      : {}),
  });
}

/** Whether two models filter the same: the same active rows (in any order) and the same settings. */
export function areFilterModelsEqual(
  a: GridFilterModel,
  b: GridFilterModel,
  excludeField?: string
): boolean {
  const aItems = getEffectiveFilters(a.items ?? [], excludeField).map(describeFilterItem).sort();
  const bItems = getEffectiveFilters(b.items ?? [], excludeField).map(describeFilterItem).sort();

  if (aItems.length !== bItems.length) return false;
  if (aItems.length === 0) return true;

  return (
    aItems.every((aItem, index) => aItem === bItems[index]) &&
    describeFilterModelSettings(a) === describeFilterModelSettings(b)
  );
}
